import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import authService from '../../services/authService';
import { useAppConfig } from '../../contexts/AppConfigContext';

interface EmbedTokenRouteProps {
  children: ReactNode;
}

type VerificationState = 'pending' | 'verified' | 'failed';

/**
 * EmbedTokenRoute — route guard for iframe-embedded pages authenticated via a
 * short-lived embed token supplied as the `embedToken` URL query parameter.
 *
 * Background: iOS Safari partitions localStorage per top-level site, so a
 * Routemize oidc session stored during a normal top-level visit is invisible
 * inside a DripJobs iframe pointing at {tenant}.routemize.com.  The usual
 * TenantOnlyRoute → /login redirect would then hit X-Frame-Options: SAMEORIGIN
 * and produce a blocked-frame error.
 *
 * This component bypasses that constraint: it reads the token from the URL,
 * validates its exp claim, and hands it to authService (in-memory only — never
 * localStorage) so every downstream REST call and the SignalR hub authenticate
 * as the intended user without any interactive login.
 *
 * The exp-claim check is purely a shape/freshness check — it decodes the JWT
 * payload locally and never verifies the signature, so a truncated or corrupted
 * token that still has an intact header+payload would pass it. To actually know
 * whether there's a real, currently-authenticated user before rendering
 * children (rather than mounting the booking flow and letting every downstream
 * call fail with 401s), this forces AppConfigContext to refetch — the same
 * /api/app/appConfiguration call and the same shared state every
 * other part of the app reads via useAppConfig().config.currentUser — and
 * only renders children once that comes back with isAuthenticated + an id.
 *
 * Deliberately NOT a separate/parallel call: AppConfigProvider wraps the whole
 * app and fires its own initial fetch on mount, before this component has had
 * a chance to set the embed token — so that first fetch can come back
 * unauthenticated and get cached. Calling refreshConfig(true) here forces a
 * refetch through the *same* context (rather than a call only this component
 * would see), so the corrected, authenticated result actually reaches every
 * other consumer of useAppConfig(), not just this route's own check.
 *
 * Important: this component NEVER redirects to /login. That would hit the same
 * X-Frame-Options wall. Instead it renders a user-visible error state inside
 * the iframe so the user knows to reopen the link from DripJobs.
 *
 * Props shape is intentionally identical to TenantOnlyRoute so the two are
 * drop-in interchangeable in App.tsx.
 */
const EmbedTokenRoute: React.FC<EmbedTokenRouteProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('embedToken');

  // Decode the exp claim using the same base64url approach as
  // ImpersonationManager.getTokenExpiry() — works for standard JWTs only;
  // opaque tokens (no dot-separated segments) are treated as invalid.
  const tokenExpiry = useMemo<Date | null>(() => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch {
      return null;
    }
  }, [token]);

  // A token is valid only if it exists, has a parseable exp, and hasn't lapsed.
  // This is a shape check only — see the verification call below for the real check.
  const isValid = !!token && !!tokenExpiry && new Date() < tokenExpiry;

  const [verification, setVerification] = useState<VerificationState>('pending');
  const { refreshConfig } = useAppConfig();

  useEffect(() => {
    if (!isValid || !token) {
      return;
    }

    let cancelled = false;
    authService.setEmbedToken(token);

    refreshConfig(true)
      .then((freshConfig) => {
        if (cancelled) return;
        // Same check every other part of the app relies on via
        // useAppConfig().config.currentUser — anything else is an auth failure.
        if (freshConfig?.currentUser?.isAuthenticated && freshConfig.currentUser.id) {
          setVerification('verified');
        } else {
          authService.clearEmbedToken();
          setVerification('failed');
        }
      })
      .catch(() => {
        if (cancelled) return;
        authService.clearEmbedToken();
        setVerification('failed');
      });

    return () => {
      cancelled = true;
    };
    // refreshConfig (AppConfigContext's fetchConfig) is a new function reference on every
    // render of AppConfigProvider, and calling it here triggers exactly that re-render —
    // including it would re-fire this effect every time, calling refreshConfig again,
    // forever. Only isValid/token should ever re-trigger verification. AppConfigContext's
    // own internal effects already omit fetchConfig from their deps for the same reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, token]);

  if (!isValid || verification === 'failed') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-50 p-6">
        <div className="text-center">
          <p className="text-base font-semibold text-ink-900">This link has expired</p>
          <p className="mt-1 text-sm text-ink-500">
            Reopen from DripJobs to get a new session link.
          </p>
        </div>
      </div>
    );
  }

  if (verification !== 'verified') {
    // Still checking with the server — avoid rendering children (and letting
    // them start firing API calls) before we actually know there's a valid user.
    return null;
  }

  return <>{children}</>;
};

export default EmbedTokenRoute;
